import type { FieldDef } from "#shared/schemas/fieldDef.ts";
import type { FieldValueData } from "#shared/schemas/fieldValue.ts";
import type { EntityDetail } from "#shared/schemas/entityDetail.ts";
import { STATUS_OPTIONS } from "#shared/constants.ts";
import { countryLabel } from "#shared/countries.ts";
import { formatFact, presentFacts } from "./facts.ts";

export const EXPORT_FORMATS = ["json", "csv", "text"] as const;

export type ExportFormat = (typeof EXPORT_FORMATS)[number];

export interface ExportRow {
  section: string;
  label: string;
  value: string;
}

const STATUS_LABEL = new Map(STATUS_OPTIONS.map((s) => [s.id, s.label]));

/** How a stored field value reads in the panel, so an export cannot drift from it. */
export function formatFieldValue(def: FieldDef | undefined, value: FieldValueData): string {
  if (value === null) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return value.toLocaleString();

  const options = def?.config?.options ?? [];
  const labelOf = (id: string) => options.find((o) => o.id === id)?.label ?? id;

  if (Array.isArray(value)) return value.length === 0 ? "—" : value.map(labelOf).join(", ");
  if (def?.type === "select") return labelOf(value);
  if (def?.type === "country" && typeof value === "string") return countryLabel(value);
  return value;
}

/**
 * The flat rows every format is built from. One source means CSV, text, and JSON
 * always agree with what the sheet shows.
 */
export function buildExportRows(detail: EntityDetail, defs: readonly FieldDef[]): ExportRow[] {
  const rows: ExportRow[] = [];

  for (const fact of presentFacts(detail.facts)) {
    rows.push({ section: "Details", label: fact.label, value: formatFact(fact) });
  }

  if (detail.status) {
    rows.push({
      section: "Pipeline",
      label: "Status",
      value: STATUS_LABEL.get(detail.status.status) ?? detail.status.status,
    });
    if (detail.status.follow_up) {
      rows.push({ section: "Pipeline", label: "Follow up", value: detail.status.follow_up });
    }
    if (detail.status.drive_url) {
      rows.push({ section: "Pipeline", label: "Drive folder", value: detail.status.drive_url });
    }
  }

  const byKey = new Map(defs.map((def) => [def.key, def]));
  for (const [key, value] of Object.entries(detail.values)) {
    if (value === null) continue;
    const def = byKey.get(key);
    rows.push({
      section: def?.group_name ?? "Fields",
      label: def?.label ?? key,
      value: formatFieldValue(def, value),
    });
  }

  for (const genre of detail.genres) {
    rows.push({
      section: "Genres",
      label: genre.name,
      value: genre.source === "user" ? "yours" : `${genre.source} suggestion`,
    });
  }

  for (const block of detail.providers) {
    for (const fact of presentFacts(block.facts)) {
      rows.push({
        section: `${block.label} (${block.source})`,
        label: fact.label,
        value: formatFact(fact),
      });
    }
  }

  return rows;
}

function csvCell(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}

export function serializeExport(
  detail: EntityDetail,
  rows: readonly ExportRow[],
  format: ExportFormat,
): string {
  if (format === "csv") {
    return [
      ["Section", "Label", "Value"].join(","),
      ...rows.map((row) => [row.section, row.label, row.value].map(csvCell).join(",")),
    ].join("\n");
  }

  if (format === "text") {
    const lines: string[] = [detail.title];
    if (detail.subtitle) lines.push(detail.subtitle);
    let section = "";
    for (const row of rows) {
      if (row.section !== section) {
        section = row.section;
        lines.push("", section.toUpperCase());
      }
      lines.push(`  ${row.label}: ${row.value}`);
    }
    return lines.join("\n");
  }

  const grouped: Record<string, Record<string, string>> = {};
  for (const row of rows) {
    const bag = grouped[row.section] ?? {};
    bag[row.label] = row.value;
    grouped[row.section] = bag;
  }

  return JSON.stringify(
    {
      entity_type: detail.entity_type,
      entity_id: detail.entity_id,
      title: detail.title,
      subtitle: detail.subtitle,
      external_url: detail.external_url,
      sections: grouped,
    },
    null,
    2,
  );
}

export function exportFilename(detail: EntityDetail, format: ExportFormat): string {
  const slug =
    detail.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 60) || detail.entity_id;
  return `${detail.entity_type}-${slug}.${format === "text" ? "txt" : format}`;
}
